import React, { useState, useCallback } from 'react';
import Modal from 'react-modal';
import { Upload, FileText, AlertTriangle, CheckCircle, Play, Eye, ArrowLeft } from 'lucide-react';

const ImportKreativManagement = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const handleFileChange = useCallback((e) => {
        const selected = e.target.files[0];
        if (selected && (selected.name.endsWith('.csv') || selected.type === 'text/csv')) {
            setFile(selected);
            setError(null);
            setResult(null);
        } else {
            setError('Bitte eine CSV-Datei auswählen.');
            setFile(null);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        const dropped = e.dataTransfer.files[0];
        if (dropped && (dropped.name.endsWith('.csv') || dropped.type === 'text/csv')) {
            setFile(dropped);
            setError(null);
            setResult(null);
        } else {
            setError('Bitte eine CSV-Datei auswählen.');
        }
    }, []);

    const runImport = async (dryRun = false) => {
        if (!file) return;

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('csvfile', file);

            const url = dryRun
                ? 'index.php/contacts/importKreativManagement?dryrun=1'
                : 'index.php/contacts/importKreativManagement';

            const response = await fetch(url, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                setError(data.error || 'Import fehlgeschlagen.');
            } else {
                setResult(data);
            }
        } catch (err) {
            setError('Netzwerkfehler: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="mb-4">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                        <a
                            href="index.php/contacts/contactlist"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                fontSize: '0.875rem',
                                color: 'var(--text-secondary, #64748b)',
                                marginBottom: '0.75rem',
                                textDecoration: 'none',
                            }}
                        >
                            <ArrowLeft size={16} />
                            Zurück zu Kontakte
                        </a>
                        <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                            <Upload size={32} className="icon-contacts" />
                            Import: Kreativ.Management
                        </h1>
                        <p className="text-muted mb-0">
                            Importiere Kontakte, Jobs und Hochzeiten aus einer Kreativ.Management CSV-Export-Datei.
                        </p>
                    </div>
                </div>
            </div>

            {/* Info-Box */}
            <div style={{
                background: 'var(--bg-card, #ffffff)',
                border: '1px solid var(--border-light, #e2e8f0)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
            }}>
                <h5 className="text-dark mb-3" style={{ fontWeight: 600 }}>
                    <AlertTriangle size={18} className="me-2" style={{ color: '#e67e22' }} />
                    Wichtige Hinweise
                </h5>
                <ul style={{ color: 'var(--text-secondary, #64748b)', fontSize: '0.9rem', marginBottom: 0 }}>
                    <li className="mb-2">Die CSV muss den <strong>Kreativ.Management Export-Format</strong> entsprechen (Semikolon-getrennt, 3 Header-Zeilen)</li>
                    <li className="mb-2"><strong>Testlauf empfohlen:</strong> Nutze zuerst "Vorschau", um das Ergebnis zu prüfen bevor Daten geschrieben werden</li>
                    <li className="mb-2">Subkontakte werden als eigene Kontakte angelegt und mit dem Hauptkontakt verknüpft</li>
                    <li className="mb-2">Felder ohne direkte Entsprechung (Extra-Telefonnummern, Sprache, etc.) werden in den Notizen gespeichert</li>
                    <li>Auftragstyp "Hochzeit" → Hochzeit, alle anderen → Job</li>
                </ul>
            </div>

            {/* Upload-Bereich */}
            <div style={{
                background: 'var(--bg-card, #ffffff)',
                border: '1px solid var(--border-light, #e2e8f0)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '1.5rem',
            }}>
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    style={{
                        border: '2px dashed var(--border-medium, #cbd5e1)',
                        borderRadius: '8px',
                        padding: '3rem 2rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: file ? 'var(--bg-secondary, #f5f7f9)' : 'transparent',
                        transition: 'background 0.2s',
                    }}
                    onClick={() => document.getElementById('csv-upload').click()}
                >
                    <input
                        id="csv-upload"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />

                    {file ? (
                        <div>
                            <FileText size={48} style={{ color: 'var(--accent-slate, #5b7c8d)', marginBottom: '1rem' }} />
                            <p className="text-dark mb-1" style={{ fontWeight: 600 }}>{file.name}</p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                                {(file.size / 1024).toFixed(1)} KB — Klicken um andere Datei zu wählen
                            </p>
                        </div>
                    ) : (
                        <div>
                            <Upload size={48} style={{ color: 'var(--text-secondary, #64748b)', marginBottom: '1rem' }} />
                            <p className="text-dark mb-1" style={{ fontWeight: 600 }}>CSV-Datei hierher ziehen</p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                                oder klicken um Datei auszuwählen
                            </p>
                        </div>
                    )}
                </div>

                {/* Buttons */}
                {file && (
                    <div className="d-flex gap-3 mt-4 justify-content-center">
                        <button
                            onClick={() => runImport(true)}
                            disabled={loading}
                            className="btn btn-outline-primary d-flex align-items-center gap-2"
                        >
                            <Eye size={18} />
                            {loading ? 'Läuft...' : 'Vorschau (Testlauf)'}
                        </button>
                        <button
                            onClick={() => setShowConfirmModal(true)}
                            disabled={loading}
                            className="btn btn-primary d-flex align-items-center gap-2"
                        >
                            <Play size={18} />
                            {loading ? 'Importiert...' : 'Import starten'}
                        </button>
                    </div>
                )}
            </div>

            {/* Fehler */}
            {error && (
                <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '12px',
                    padding: '1rem 1.5rem',
                    marginBottom: '1.5rem',
                    color: '#dc2626',
                }}>
                    <AlertTriangle size={18} className="me-2" />
                    {error}
                </div>
            )}

            {/* Ergebnis */}
            {result && (
                <div style={{
                    background: 'var(--bg-card, #ffffff)',
                    border: '1px solid var(--border-light, #e2e8f0)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                }}>
                    {/* Stats Header */}
                    <div style={{
                        background: result.dryRun ? '#fef9c3' : '#dcfce7',
                        padding: '1rem 1.5rem',
                        borderBottom: '1px solid var(--border-light, #e2e8f0)',
                    }}>
                        <h5 className="mb-2" style={{ fontWeight: 600, color: result.dryRun ? '#854d0e' : '#166534' }}>
                            <CheckCircle size={18} className="me-2" />
                            {result.dryRun ? 'Vorschau-Ergebnis (keine Daten geschrieben)' : 'Import abgeschlossen!'}
                        </h5>
                        <div className="d-flex flex-wrap gap-4" style={{ fontSize: '0.9rem' }}>
                            <span><strong>{result.stats.contacts_imported}</strong> Kontakte</span>
                            <span><strong>{result.stats.subcontacts_imported}</strong> Subkontakte</span>
                            <span><strong>{result.stats.jobs_created}</strong> Jobs</span>
                            <span><strong>{result.stats.weddings_created}</strong> Hochzeiten</span>
                            <span><strong>{result.stats.leads_created}</strong> Anfragen</span>
                            {result.stats.skipped_rows > 0 && (
                                <span style={{ color: '#b45309' }}><strong>{result.stats.skipped_rows}</strong> übersprungen</span>
                            )}
                        </div>
                    </div>

                    {/* Log */}
                    <div style={{ padding: '1.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                        <h6 className="text-dark mb-3" style={{ fontWeight: 600 }}>Import-Log</h6>
                        <pre style={{
                            fontSize: '0.8rem',
                            color: 'var(--text-secondary, #64748b)',
                            background: 'var(--bg-secondary, #f5f7f9)',
                            padding: '1rem',
                            borderRadius: '8px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                        }}>
                            {result.log.join('\n')}
                        </pre>
                    </div>
                </div>
            )}

            {/* Bestätigungs-Modal */}
            <Modal
                isOpen={showConfirmModal}
                onRequestClose={() => setShowConfirmModal(false)}
                ariaHideApp={false}
                style={{
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1050,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem'
                    },
                    content: {
                        position: 'relative',
                        inset: 'auto',
                        maxWidth: '480px',
                        width: '100%',
                        padding: 0,
                        border: 'none',
                        borderRadius: '12px',
                        background: 'var(--bg-card, #ffffff)',
                        overflow: 'hidden'
                    }
                }}
            >
                <div className="modal-content">
                <div className="modal-body" style={{ padding: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                        <AlertTriangle size={24} style={{ color: 'var(--accent-orange, #e67e22)', flexShrink: 0 }} />
                        <h5 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>
                            Import starten?
                        </h5>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9375rem', color: 'var(--text-secondary, #64748b)', lineHeight: 1.5 }}>
                        Kontakte, Jobs und Hochzeiten werden angelegt. Dieser Vorgang kann nicht rückgängig gemacht werden.
                    </p>
                </div>
                <div className="modal-footer" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.75rem',
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid var(--border-light, #e2e8f0)'
                }}>
                    <button
                        onClick={() => setShowConfirmModal(false)}
                        className="btn btn-outline-secondary"
                    >
                        Abbrechen
                    </button>
                    <button
                        onClick={() => {
                            setShowConfirmModal(false);
                            runImport(false);
                        }}
                        className="btn btn-primary d-flex align-items-center gap-2"
                    >
                        <Play size={16} />
                        Import starten
                    </button>
                </div>
                </div>
            </Modal>
        </div>
    );
};

export default ImportKreativManagement;
